import * as THREE from 'three';
import { EntityType, RACE_INFO, SHIP_CLASS_KEY, type RaceId } from '@viral-space/shared';
import type { ClientEntity } from '../game/state';
import type { GameState } from '../game/state';

/**
 * Coerce a per-entity `extra.shipClass` payload (could be a numeric
 * enum from the server's compact encoding, or a pre-resolved string,
 * or missing entirely) into an uppercase string suitable for HUD
 * rendering.
 *
 * The previous code path:
 *
 *   const shipClass = (extra['shipClass'] as string ?? 'fighter').toUpperCase();
 *
 * was the source of a per-frame `TypeError: toUpperCase is not a
 * function` that the user saw as "the game freezes while moving on
 * a planet" — the world-labels update loop runs inside the main
 * render frame, so every uncaught throw aborts that frame's label
 * pass, blows out the V8 inline-cache for the surrounding closure,
 * and (when the throw is sustained across many frames as it is here:
 * every visible NPC re-triggers it) produces exactly the 30–2200 ms
 * idle-on-surface stutters the user reported. The `as string`
 * assertion lied about the runtime shape — surface NPCs ship their
 * class through the compact wire format as the enum's numeric value.
 */
function normalizeShipClassLabel(raw: unknown): string {
  if (typeof raw === 'string') return raw.toUpperCase();
  if (typeof raw === 'number') {
    const slug = (SHIP_CLASS_KEY as Record<number, string>)[raw];
    return (slug ?? 'fighter').toUpperCase();
  }
  return 'FIGHTER';
}

const FONT = "'Share Tech Mono', monospace";

const TYPE_INFO: Record<number, { label: string; color: string; icon: string }> = {
  [EntityType.Ship]: { label: 'SHIP', color: '#44ddff', icon: '◆' },
  [EntityType.NPC]: { label: 'NPC', color: '#ffff22', icon: '◆' },
  [EntityType.Asteroid]: { label: 'ASTEROID', color: '#aaaaaa', icon: '◇' },
  [EntityType.Planet]: { label: 'PLANET', color: '#44aa88', icon: '●' },
  [EntityType.Star]: { label: 'STAR', color: '#ffdd44', icon: '★' },
  [EntityType.Station]: { label: 'STATION', color: '#00ddff', icon: '▣' },
  [EntityType.WarpGate]: { label: 'WARP GATE', color: '#bb77ff', icon: '◎' },
  [EntityType.Comet]: { label: 'COMET', color: '#aaddff', icon: '☄' },
  [EntityType.ResourceDrop]: { label: 'RESOURCE', color: '#00ffaa', icon: '✦' },
  [EntityType.Projectile]: { label: '', color: '', icon: '' },
};

const NEARBY_LABEL_DIST = 100;
const CROSSHAIR_LABEL_DIST = 500;
const MAX_LABELS = 10;
const CROSSHAIR_THRESHOLD = 120;

/**
 * Render a race / civilization color as CSS, brightening it toward white
 * if the raw hue would be unreadable against the label's near-black
 * `rgba(0,0,0,0.6)` background.
 *
 * Several races use intentionally dark `primaryColor`s to evoke "void /
 * stealth / abyssal" vibes (Singularity Sovereign is literally pure
 * black, Abyssal Chorus and Void Walker are near-black). Painting the
 * label text in those colors makes the race tag invisible. We mix the
 * raw color toward white by the smallest amount needed to clear a
 * Rec.709 perceived-luminance threshold so the hue is preserved (a
 * Sovereign label still reads as red-tinted, an Abyssal label still
 * reads as deep teal) while always being legible.
 *
 * Closed-form: `Y(mix) = Y(orig) + t * (1 − Y(orig))`, solved for the
 * smallest `t ∈ [0, 1]` that hits the threshold. No iteration / no
 * channel-clipping artifacts.
 */
function readableRaceColor(rawHex: number, minLuminance = 0.55): string {
  const r0 = (rawHex >> 16) & 0xff;
  const g0 = (rawHex >> 8) & 0xff;
  const b0 = rawHex & 0xff;
  const lum =
    (0.2126 * r0 + 0.7152 * g0 + 0.0722 * b0) / 255;
  if (lum >= minLuminance) {
    return `#${rawHex.toString(16).padStart(6, '0')}`;
  }
  const t = Math.min(1, Math.max(0, (minLuminance - lum) / (1 - lum)));
  const r = Math.round(r0 * (1 - t) + 255 * t);
  const g = Math.round(g0 * (1 - t) + 255 * t);
  const b = Math.round(b0 * (1 - t) + 255 * t);
  const out = (r << 16) | (g << 8) | b;
  return `#${out.toString(16).padStart(6, '0')}`;
}

interface LabelEntry {
  el: HTMLElement;
  entityId: string;
  /**
   * Hash of the last-rendered HTML body. Comparing against the freshly
   * computed key short-circuits the expensive `innerHTML = …` write
   * (which otherwise reparses the markup, builds a fresh DOM subtree,
   * and forces layout for every visible label every frame). Updating
   * the position styles only is essentially free; it's the
   * `innerHTML` rewrite that was the hot path during surface flight.
   */
  contentKey: string;
  /**
   * Last opacity bucket we wrote to the DOM (rounded to two decimals
   * for nearby fade-out). Style writes are layout-affecting on at
   * least Chrome's compositor layer pipeline, so skipping the write
   * when the bucket hasn't changed visibly removes another per-frame
   * source of compositing churn.
   */
  opacityKey: number;
  /** Whether the border + bg style block matches the "is target" state. */
  targetState: boolean;
}

const SHOWN_DISPLAY = '';
const HIDDEN_DISPLAY = 'none';

export interface TargetInfo {
  entity: ClientEntity;
  distance: number;
  screenX: number;
  screenY: number;
}

interface ProjectedEntry {
  entity: ClientEntity;
  sx: number;
  sy: number;
  dist: number;
  screenDist: number;
}

export class WorldLabels {
  private container: HTMLElement;
  private labels = new Map<string, LabelEntry>();
  private pool: HTMLElement[] = [];
  private _currentTarget: TargetInfo | null = null;
  /**
   * Reusable scratch state for `update()`. The label loop runs at
   * 60 Hz and used to allocate two arrays + a Set + a Vector3 + one
   * `{entity, sx, sy, dist, screenDist}` literal per visible entity
   * every frame; with 20–50 entities in label range that adds up to
   * thousands of short-lived objects per second, which the user
   * eventually saw as a GC stutter while flying. Holding the
   * scratch state on the instance lets us reuse all of it.
   */
  private _scratchAll: ProjectedEntry[] = [];
  private _scratchVisible: ProjectedEntry[] = [];
  private _scratchActiveIds = new Set<string>();
  private _scratchVec = new THREE.Vector3();
  /**
   * Set of unique error keys we've already logged. Lets the per-label
   * try/catch in `update()` swallow a sustained throw scenario
   * (e.g. malformed wire data) without console-spamming the user one
   * line per entity per frame.
   */
  private _loggedLabelErrors = new Set<string>();

  constructor(container: HTMLElement) {
    this.container = container;
  }

  get currentTarget(): TargetInfo | null {
    return this._currentTarget;
  }

  update(
    gameState: GameState,
    camera: THREE.PerspectiveCamera,
    screenWidth: number,
    screenHeight: number,
  ): void {
    const player = gameState.getPlayerEntity();
    if (!player) {
      this.hideAll();
      this._currentTarget = null;
      return;
    }

    const playerPos = player.transform.position;
    const entities = gameState.getEntities();
    const localId = gameState.getLocalPlayerId();

    // Reuse scratch arrays. We extend the underlying array as needed
    // (so the high-water-mark of label-eligible entities is what
    // sticks around) and reuse the per-row object literals so the
    // hot loop doesn't allocate. We track the *logical* length
    // separately because we don't want to truncate the Array (that
    // would null out reusable row objects).
    const all = this._scratchAll;
    let allLen = 0;
    const tmpVec = this._scratchVec;
    const cx = screenWidth / 2;
    const cy = screenHeight / 2;

    for (const [id, entity] of entities) {
      if (id === localId) continue;
      if (entity.type === EntityType.Projectile) continue;

      const info = TYPE_INFO[entity.type];
      if (!info || !info.label) continue;

      const pos = entity.transform.position;
      const dx = pos.x - playerPos.x;
      const dy = pos.y - playerPos.y;
      const dz = pos.z - playerPos.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

      if (dist > CROSSHAIR_LABEL_DIST) continue;

      tmpVec.set(pos.x, pos.y, pos.z);
      tmpVec.project(camera);

      if (tmpVec.z > 1) continue;

      const sx = (tmpVec.x * 0.5 + 0.5) * screenWidth;
      const sy = (-tmpVec.y * 0.5 + 0.5) * screenHeight;

      if (sx < -100 || sx > screenWidth + 100 || sy < -100 || sy > screenHeight + 100) continue;

      const dxScreen = sx - cx;
      const dyScreen = sy - cy;
      const screenDist = Math.sqrt(dxScreen * dxScreen + dyScreen * dyScreen);

      let row = all[allLen];
      if (!row) {
        row = { entity, sx, sy, dist, screenDist };
        all[allLen] = row;
      } else {
        row.entity = entity;
        row.sx = sx;
        row.sy = sy;
        row.dist = dist;
        row.screenDist = screenDist;
      }
      allLen++;
    }

    // Find crosshair target (closest to center within threshold, any range)
    let closestToCrosshair: ProjectedEntry | null = null;
    let closestCrosshairDist = Infinity;

    for (let i = 0; i < allLen; i++) {
      const p = all[i]!;
      if (p.screenDist < CROSSHAIR_THRESHOLD && p.screenDist < closestCrosshairDist) {
        closestCrosshairDist = p.screenDist;
        closestToCrosshair = p;
      }
    }

    // Only show labels for: (a) nearby objects, or (b) the crosshair target
    const visible = this._scratchVisible;
    let visibleLen = 0;
    for (let i = 0; i < allLen; i++) {
      const p = all[i]!;
      const isTarget = closestToCrosshair?.entity.id === p.entity.id;
      if (isTarget || p.dist <= NEARBY_LABEL_DIST) {
        visible[visibleLen++] = p;
      }
    }

    // Sort only the populated prefix and cap at MAX_LABELS without
    // truncating the Array (truncating would drop our reusable row
    // pointers).
    visible.length = visibleLen;
    visible.sort((a, b) => a.dist - b.dist);
    if (visibleLen > MAX_LABELS) visibleLen = MAX_LABELS;

    if (closestToCrosshair) {
      this._currentTarget = {
        entity: closestToCrosshair.entity,
        distance: closestToCrosshair.dist,
        screenX: closestToCrosshair.sx,
        screenY: closestToCrosshair.sy,
      };
    } else {
      this._currentTarget = null;
    }

    const activeIds = this._scratchActiveIds;
    activeIds.clear();

    for (let vi = 0; vi < visibleLen; vi++) {
      const p = visible[vi]!;
      // Belt-and-suspenders: wrap the per-label work in a try/catch
      // so any unexpected runtime error on ONE entity can't abort the
      // rest of the loop. This loop runs every frame inside the main
      // render tick — an uncaught throw here used to manifest as the
      // user-reported "ship freezes for 30–2200 ms while standing
      // still on a planet" because the throw burned the frame budget
      // and (because V8 deopts the surrounding closure when it sees
      // repeated throws) tanked subsequent frames too. The defensive
      // `typeof` checks elsewhere in this method are the primary fix;
      // this catch is the safety net for any future wire-format drift.
      try {
      activeIds.add(p.entity.id);
      let entry = this.labels.get(p.entity.id);

      if (!entry) {
        const el = this.acquireLabel();
        entry = { el, entityId: p.entity.id, contentKey: '', opacityKey: -1, targetState: false };
        this.labels.set(p.entity.id, entry);
        this.container.appendChild(el);
      }

      const info = TYPE_INFO[p.entity.type]!;
      const isTarget = closestToCrosshair?.entity.id === p.entity.id;
      const name = p.entity.name ?? info.label;
      // Bucket distance so a moving entity doesn't force a 60 Hz
      // string rebuild + innerHTML reparse. <10 m we keep one
      // decimal of precision (UX wants the close-quarters number to
      // tick); 10–99 m round to whole meters; ≥100 m round to the
      // nearest 5 m (the label font is too small for finer numbers
      // to be readable while flying anyway). Same numbers the
      // crosshair distance HUD uses elsewhere.
      const distNum = p.dist;
      let distStr: string;
      if (distNum < 10) distStr = distNum.toFixed(1);
      else if (distNum < 100) distStr = Math.round(distNum).toString();
      else distStr = (Math.round(distNum / 5) * 5).toString();

      let civLine = '';
      const extra = p.entity.extra ?? {};
      if (p.entity.type === EntityType.Planet) {
        // Defensive `typeof` instead of an `as string` cast — the
        // wire format occasionally hands back a number for civ ids
        // and the previous `.toUpperCase()` call would throw the
        // same per-frame TypeError that was killing surface fps.
        const civRaw = extra['civilizationName'];
        const civName = typeof civRaw === 'string' && civRaw.length > 0 ? civRaw : null;
        if (civName) {
          const rId = extra['raceId'] as number | undefined;
          const rawColor = rId !== undefined
            ? ((RACE_INFO as Record<number, { primaryColor: number }>)[rId]?.primaryColor ?? 0x44aa88)
            : 0x44aa88;
          const rColor = rId !== undefined ? readableRaceColor(rawColor) : info.color;
          civLine = `<br><span style="color:${rColor};font-size:8px">${civName.toUpperCase()} WORLD</span>`;
        }
      } else if (p.entity.type === EntityType.Ship) {
        const playerLvl = extra['playerLevel'] as number | undefined;
        const rId = extra['raceId'] as number | undefined;
        if (playerLvl != null) {
          const rawColor = rId !== undefined
            ? ((RACE_INFO as Record<number, { primaryColor: number }>)[rId]?.primaryColor ?? 0x44ddff)
            : 0x44ddff;
          const rColor = readableRaceColor(rawColor);
          civLine = `<br><span style="color:${rColor};font-size:8px">LV.${playerLvl} PILOT</span>`;
        }
      } else if (p.entity.type === EntityType.NPC) {
        const rId = extra['raceId'] as number | undefined;
        const npcLvl = extra['npcLevel'] as number | undefined;
        // ── Defensive shipClass coercion ─────────────────────────────
        // See `normalizeShipClassLabel` above — surface NPCs send the
        // class as a NUMBER through the compact wire format, and the
        // previous `(... as string).toUpperCase()` threw on every NPC,
        // every frame. That was the dominant freeze the user reported.
        const shipClass = normalizeShipClassLabel(extra['shipClass']);
        const lvlTag = npcLvl != null ? `LV.${npcLvl} ` : '';
        const isMothership = shipClass === 'MOTHERSHIP';
        if (rId !== undefined) {
          const raceRaw = (RACE_INFO as Record<number, { name?: string }>)[rId]?.name;
          const raceName = typeof raceRaw === 'string' ? raceRaw.toUpperCase() : '';
          const rawColor = (RACE_INFO as Record<number, { primaryColor: number }>)[rId]?.primaryColor ?? 0xffff22;
          const rColor = readableRaceColor(rawColor);
          const bossTag = isMothership
            ? `<br><span style="color:#ff3344;font-size:9px;letter-spacing:2px;">⚠ CAPITAL SHIP ⚠</span>`
            : '';
          civLine = `<br><span style="color:${rColor};font-size:8px">${lvlTag}${raceName} ${shipClass}</span>${bossTag}`;
        } else if (npcLvl != null) {
          civLine = `<br><span style="color:#ffff22;font-size:8px">${lvlTag}</span>`;
        }
      }

      const labelColor = (p.entity.type === EntityType.NPC && p.entity.disposition === 'hostile')
        ? '#ff4444' : info.color;

      // Build the content key BEFORE the markup so we can short-circuit
      // both the string assembly and the innerHTML write when nothing
      // visibly changed for this label this frame.
      const contentKey = `${labelColor}|${name}|${distStr}|${civLine}`;
      if (contentKey !== entry.contentKey) {
        entry.el.innerHTML =
          `<span style="color:${labelColor};font-size:10px">${info.icon}</span> ` +
          `<span style="color:${labelColor}">${name}</span> ` +
          `<span style="color:rgba(255,255,255,0.5);font-size:9px">${distStr}m</span>` +
          civLine;
        entry.contentKey = contentKey;
      }

      // Position writes are cheap (translate3d via CSS transform on
      // the container would be cheaper still, but `left/top` here is
      // already `position: absolute` against a layered overlay so
      // the cost is bounded to a paint, not a relayout).
      entry.el.style.left = `${p.sx}px`;
      entry.el.style.top = `${p.sy}px`;
      if (entry.el.style.display !== SHOWN_DISPLAY) {
        entry.el.style.display = SHOWN_DISPLAY;
      }

      // Border + background only flip when the target identity
      // changes — comparing the boolean is far cheaper than always
      // re-writing the rgba string.
      if (entry.targetState !== isTarget) {
        entry.el.style.borderColor = isTarget ? info.color : 'transparent';
        entry.el.style.background = isTarget
          ? 'rgba(0,0,0,0.6)'
          : 'rgba(0,0,0,0.3)';
        entry.targetState = isTarget;
      }

      // Bucket opacity to two decimals so micro-changes don't trigger
      // CSS-transition repaints every frame.
      const opacityRaw = isTarget ? 1 : Math.max(0.3, 1 - p.dist / NEARBY_LABEL_DIST);
      const opacityBucket = Math.round(opacityRaw * 100);
      if (opacityBucket !== entry.opacityKey) {
        entry.el.style.opacity = (opacityBucket / 100).toString();
        entry.opacityKey = opacityBucket;
      }
      } catch (err) {
        // Log once per error category — guards against console-spam
        // on a sustained throw scenario. The defensive coercions
        // earlier in this method should have prevented any throw
        // from reaching here in the first place; if one does, we
        // skip the affected label for this frame and keep going so
        // the rest of the HUD doesn't go dark.
        const key = err instanceof Error ? `${err.name}:${err.message}` : String(err);
        if (!this._loggedLabelErrors.has(key)) {
          this._loggedLabelErrors.add(key);
          console.warn('[world-labels] swallowed per-label error (will not repeat):', err);
        }
      }
    }

    for (const [id, entry] of this.labels) {
      if (!activeIds.has(id)) {
        entry.el.style.display = HIDDEN_DISPLAY;
        // NB: leave the element in the DOM. The previous
        // `el.remove() → pool.push() → appendChild()` cycle was a
        // major source of layout work on surfaces where labels
        // constantly cross the visibility threshold. Keeping the
        // node attached and just toggling display:none lets the
        // browser keep its compositor layer.
        entry.contentKey = '';
        entry.opacityKey = -1;
        entry.targetState = false;
        this.pool.push(entry.el);
        this.labels.delete(id);
      }
    }
  }

  private acquireLabel(): HTMLElement {
    if (this.pool.length > 0) {
      return this.pool.pop()!;
    }
    const el = document.createElement('div');
    Object.assign(el.style, {
      position: 'absolute',
      transform: 'translate(-50%, -100%) translateY(-12px)',
      fontFamily: FONT,
      fontSize: '10px',
      color: '#ffffff',
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      padding: '2px 6px',
      borderRadius: '2px',
      border: '1px solid transparent',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      textShadow: '0 0 4px rgba(0,0,0,0.8)',
      transition: 'opacity 0.15s ease',
      zIndex: '5',
    });
    return el;
  }

  private hideAll(): void {
    for (const [id, entry] of this.labels) {
      entry.el.style.display = HIDDEN_DISPLAY;
      entry.contentKey = '';
      entry.opacityKey = -1;
      entry.targetState = false;
      this.pool.push(entry.el);
      this.labels.delete(id);
    }
  }

  dispose(): void {
    // On full teardown actually rip the nodes out of the DOM so the
    // overlay container is empty between sessions / scene reloads.
    for (const entry of this.labels.values()) {
      entry.el.remove();
    }
    this.labels.clear();
    for (const el of this.pool) {
      el.remove();
    }
    this.pool.length = 0;
  }
}
